import React, { createContext, useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom"; 
import { getData } from "../fetchRoutes/getData";

export const AuctionSearchAndFilterContext = createContext();

export default function AuctionSearchAndFilterProvider(props) {
    const filterUrl = process.env.REACT_APP_FILTER_AUCTIONS;
    const [searchParams, setSearchParams] = useSearchParams();

    // Function to get filters from URL OR localStorage
    const getStoredFilters = () => {
        const savedFilters = JSON.parse(localStorage.getItem("auctionFilters")) || {};
        return {
            beds: searchParams.get("beds") || savedFilters.beds || "",
            price: searchParams.get("price") || savedFilters.price || "",
            bids: searchParams.get("bids") || savedFilters.bids || "",
            reservePrice: searchParams.get("reservePrice") || savedFilters.reservePrice || "",
            auctionStatus: searchParams.get("auctionStatus") || savedFilters.auctionStatus || "open", // Default to "open"
        };
    };

    const [searchQuery, setSearchQuery] = useState(searchParams.get("location") || localStorage.getItem("auctionSearchQuery") || "");
    const [filterQueries, setFilterQueries] = useState(getStoredFilters);
    const [filter, setFilter] = useState(false);

    // Save to localStorage when filters change
    useEffect(() => {
        localStorage.setItem("auctionSearchQuery", searchQuery);
        localStorage.setItem("auctionFilters", JSON.stringify(filterQueries));

        setSearchParams((prevParams) => {
            const params = new URLSearchParams(prevParams);

            if (searchQuery) params.set("location", searchQuery);
            else params.delete("location");

            Object.entries(filterQueries).forEach(([key, value]) => {
                if (value) params.set(key, value);
                else params.delete(key);
            });

            return params;
        });
    }, [searchQuery, filterQueries, setSearchParams]);

    // Load from localStorage when component mounts
    useEffect(() => {
        const savedQuery = localStorage.getItem("auctionSearchQuery") || "";
        const savedFilters = JSON.parse(localStorage.getItem("auctionFilters")) || {};

        setSearchQuery(savedQuery);
        setFilterQueries(savedFilters);
    }, []); // Runs only once on mount

    // Handle the change for searchQuery (location)
    const handleChange = (event) => {
        setSearchQuery(event.target.value); 
    };

    // Handle filter query changes
    const handleFilterChange = (event) => {
        const { name, value } = event.target;
        setFilterQueries((prev) => ({ ...prev, [name]: value })); 
    };

    // Perform the filter action (fetching filtered auctions)
    const handleFilteration = async (setFilteredAuctions) => {
        setFilter(true);
        const queryString = searchParams.toString() ? `?${searchParams.toString()}` : "";

        try {
            const response = await getData(`${filterUrl}${queryString}`);
            if (response.status === "success") {
                setFilteredAuctions(response.data);
            } else {
                console.error("Error fetching auctions:", response.message);
                setFilteredAuctions([]);
            }
        } catch (error) {
            console.error("Network error:", error);
            setFilteredAuctions([]);
        } finally {
            setFilter(false);
        }
    };

    // Reset the filters and the search parameters
    const handleReset = (setFilteredAuctions, auctions) => {
        setSearchQuery(""); // Clear search query
        setFilterQueries({
            beds: "",
            price: "",
            bids: "",
            reservePrice: "",
            auctionStatus: "open",
        }); 
        setSearchParams(new URLSearchParams()); 
        localStorage.removeItem("auctionSearchQuery");
        localStorage.removeItem("auctionFilters");
        setFilteredAuctions(auctions);
    };

    return (
        <AuctionSearchAndFilterContext.Provider
            value={{
                searchQuery,
                setSearchQuery,
                filterQueries,
                setFilterQueries,
                handleChange,
                handleFilterChange,
                handleFilteration,
                handleReset,
                filter,
                setFilter,
            }}
        >
            {props.children}
        </AuctionSearchAndFilterContext.Provider>
    );
}

